import React from 'react';
import { motion } from 'framer-motion';
import { COLORS, FONTS } from './theme';
import InkText from './InkText';

const EASE = [0.22, 0.61, 0.36, 1] as const;

const rv = (delay: number) => ({
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '0px 0px -8% 0px' },
  transition: { duration: 1, delay, ease: EASE },
});

// Diamond accent pinned to the gold panel's border
const CornerDiamond: React.FC<{ position: 'top' | 'bottom' }> = ({ position }) => (
  <span
    aria-hidden="true"
    className="absolute left-1/2 -ml-[5px] w-[9px] h-[9px] rotate-45"
    style={{
      backgroundColor: COLORS.goldLeaf,
      [position]: '-5px',
    }}
  />
);

const StoryContrast: React.FC = () => {
  return (
    <section className="py-[86px]">
      <div className="w-full max-w-[640px] md:max-w-[760px] mx-auto px-[26px] md:px-10">
        <motion.h2
          {...rv(0)}
          className="font-bold text-center leading-[1.15] [text-wrap:balance]"
          style={{
            fontFamily: FONTS.heading,
            color: COLORS.nearBlack,
            fontSize: 'clamp(30px, 8vw, 42px)',
          }}
        >
          Not the way we heard them
        </motion.h2>

        <motion.p
          {...rv(0.1)}
          className="text-center text-lg mt-4 mb-11 mx-auto max-w-[40ch] [text-wrap:pretty]"
          style={{ fontFamily: FONTS.body, color: COLORS.brown }}
        >
          We all grew up with these stories told one way. Paathshaala turns
          them around.
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-[22px] md:gap-[26px] md:items-stretch">
          {/* The old way — muted, faded */}
          <motion.div
            {...rv(0.15)}
            className="rounded p-[34px_28px_36px] md:p-[42px_36px] opacity-85"
            style={{
              backgroundColor: 'rgba(200,182,144,0.45)',
              border: '1px dashed rgba(93,64,55,0.4)',
            }}
          >
            <span
              className="block text-xs font-bold uppercase tracking-[0.28em] mb-[18px] opacity-75"
              style={{ fontFamily: FONTS.body, color: COLORS.brown }}
            >
              The way we heard them
            </span>
            <p
              className="text-[19px] leading-relaxed [text-wrap:pretty]"
              style={{ fontFamily: FONTS.body, color: COLORS.brown }}
            >
              Someone narrates. You sit quietly. The moral arrives at the end.
            </p>
            <p
              className="text-[17px] italic leading-relaxed mt-3.5 opacity-85"
              style={{ fontFamily: FONTS.body, color: COLORS.brown }}
            >
              Nobody ever asked what <em>you</em> thought.
            </p>
          </motion.div>

          {/* The Paathshaala way — gold, alive */}
          <motion.div
            {...rv(0.3)}
            className="relative rounded p-[34px_28px_36px] md:p-[42px_36px]"
            style={{
              backgroundColor: COLORS.parchmentDark,
              border: `1.5px solid ${COLORS.goldLeaf}`,
              boxShadow:
                '0 0 0 4px rgba(197,165,90,0.14), 0 18px 44px -18px rgba(62,39,35,0.4), inset 0 1px 0 rgba(255,244,214,0.4)',
            }}
          >
            <CornerDiamond position="top" />
            <span
              className="block text-xs font-bold uppercase tracking-[0.28em] mb-[18px]"
              style={{ fontFamily: FONTS.body, color: COLORS.goldLeafDark }}
            >
              The Paathshaala way
            </span>
            <p
              className="text-[19px] leading-relaxed [text-wrap:pretty]"
              style={{ fontFamily: FONTS.body, color: COLORS.darkBrown }}
            >
              The same tale, told through the lens of your child&rsquo;s world
              &mdash; until a question rises in their own head:
            </p>
            <InkText
              text="“What would I have done?”"
              mode="letters"
              baseDelay={350}
              step={32}
              className="font-bold italic my-[18px] leading-[1.3] [text-wrap:balance]"
              style={{
                fontFamily: FONTS.heading,
                color: COLORS.redInk,
                fontSize: 'clamp(24px, 6.4vw, 30px)',
              }}
            />
            <p
              className="text-[19px] leading-relaxed [text-wrap:pretty]"
              style={{ fontFamily: FONTS.body, color: COLORS.darkBrown }}
            >
              Suddenly it&rsquo;s not a lecture. It&rsquo;s about them &mdash;
              their instincts, their ego, their anger, their friends.
            </p>
            <CornerDiamond position="bottom" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default StoryContrast;
